import React from 'react';
import {Box, Center, Table, TableContainer, Tbody, Td, Text, Tr, Grid, Link, VStack, HStack, Badge, Spinner, Alert, AlertIcon} from '@chakra-ui/react';
import { useQuery } from '@apollo/client';
import { QUERY_MATCHES } from '../../Queries';

import { ButtonCallToAction } from '../../Components';

export const MatchesCompetition = () => {
  // Query for upcoming and in-progress matches
  const { data: upcomingData, loading: upcomingLoading, error: upcomingError } = useQuery(QUERY_MATCHES, {
    variables: {
      status: 'UPCOMING'
    }
  });

  const { data: inProgressData, loading: inProgressLoading } = useQuery(QUERY_MATCHES, {
    variables: {
      status: 'IN_PROGRESS'
    }
  });

  // Combine and sort upcoming matches by date, removing duplicates by match_id
  const allUpcomingMatches = [
    ...(upcomingData?.queryMatches || []),
    ...(inProgressData?.queryMatches || [])
  ]
    .filter((match, index, self) => 
      index === self.findIndex(m => m.match_id === match.match_id)
    )
    .sort((a, b) => a.match_details.match_date - b.match_details.match_date);

  // Group matches by month and limit to next 3 months
  const groupMatchesByMonth = (matches) => {
    const now = Date.now() / 1000;
    const threeMonthsFromNow = now + (90 * 24 * 60 * 60); // 90 days in seconds
    
    const filtered = matches.filter(m => m.match_details.match_date <= threeMonthsFromNow);
    
    const grouped = {};
    filtered.forEach(match => {
      const date = new Date(match.match_details.match_date * 1000);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          label: monthLabel,
          matches: []
        };
      }
      grouped[monthKey].matches.push(match);
    });
    
    return Object.values(grouped);
  };

  const matchesByMonth = groupMatchesByMonth(allUpcomingMatches);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'TBD';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDisciplineLabel = (discipline) => {
    const labels = {
      'CMP_HIGHPOWER': 'CMP Highpower/Service Rifle',
      'COWBOY_SILHOUETTE': 'Cowboy Silhouette',
      'BLACK_POWDER_SILHOUETTE': 'Black Powder Silhouette',
      'NRA_ACTION_PISTOL': 'NRA Action Pistol',
      'BOWLING_PIN_MATCH': 'Bowling Pin Match'
    };
    return labels[discipline] || discipline;
  };

  const getDisciplineLink = (discipline) => {
    const links = {
      'CMP_HIGHPOWER': '/matches-competition/cmp-highpower-service-rifle/schedule',
      'COWBOY_SILHOUETTE': '/matches-competition/cowboy-silhouette/schedule',
      'BLACK_POWDER_SILHOUETTE': '/matches-competition/black-powder-silhouette/schedule',
      'NRA_ACTION_PISTOL': '/matches-competition/nra-action-pistol/schedule',
      'BOWLING_PIN_MATCH': '/matches-competition/bowling-pin-match/schedule'
    };
    return links[discipline] || '/matches-competition';
  };

  const additional = {
    title: "Our Matches",
    ctas: [
      {
        title: "Cowboy Silhouette",
        subtitle: "Honoring Old Western Traditions",
        image: "https://ggc-website-public-content.s3.amazonaws.com/images/nra-silhouettes.jpg",
        buttonText: "Read More",
        buttonLink: "/matches-competition/cowboy-silhouette"
      },
      {
        title: "CMP Highpower/Service Rifle",
        subtitle: "Honoring our Nation's Warriors and Marksmen",
        image: "https://ggc-website-public-content.s3.amazonaws.com/images/service-rifle-target.jpg",
        buttonText: "Read More",
        buttonLink: "/matches-competition/cmp-highpower-service-rifle"
      },
      {
        title: "Black Powder Silhouette",
        subtitle: "A Nod to Our Traditions of Firearms",
        image: "https://ggc-website-public-content.s3.amazonaws.com/images/inset-c.jpg",
        buttonText: "Read More",
        buttonLink: "/matches-competition/black-powder-silhouette"
      },
      {
        title: "Bowling Pin Match",
        subtitle: "A Challenging Dynamic Handgun Match",
        image: "https://ggc-website-public-content.s3.amazonaws.com/images/bp_shoot_1.jpg",
        buttonText: "Read More",
        buttonLink: "/matches-competition/bowling-pin-match"
      },
      {
        title: "NRA Action Pistol",
        subtitle: "One of the Most Popular Practical, Dynamic Handgun Sports",
        image: "https://ggc-website-public-content.s3.amazonaws.com/images/inset-b.jpg",
        buttonText: "Read More",
        buttonLink: "/matches-competition/nra-action-pistol"
      },
      {
        title: "Trap League",
        subtitle: "Traditional Trap Leagues on our Best-in-Class Shotgun Facilities",
        image: "https://ggc-website-public-content.s3.amazonaws.com/images/inset-a.jpg",
        buttonText: "Read More",
        buttonLink: "/matches-competition/trap-league"
      },
      {
        title: "Skeet & Five Stand League",
        subtitle: "Five-Stand Shotgun Leagues on our Best-in-Class Shotgun Facilities",
        image: "https://ggc-website-public-content.s3.amazonaws.com/images/inset-c.jpg",
        buttonText: "Read More",
        buttonLink: "/matches-competition/five-stand-league"
      }
    ]
  };

  return (
    <Box mt={6}>
      <Text mb={6} color="black">
        We offer a variety of match types, meant to offer fun yet competitive shooting sports opportunities for shooters of all ages and skill levels. Matches are open to the public, as well as all Club Members.
      </Text>

      {/* Upcoming Matches Section */}
      <Box mb={8}>
        <Center mb={4}>
          <Text as="h3" color="#000000" fontSize={{ base: '22px' }} fontWeight="600">Upcoming Matches</Text>
        </Center>
        
        {upcomingLoading || inProgressLoading ? (
          <Center py={8}>
            <Spinner size="lg" color="orange.500" />
          </Center>
        ) : upcomingError ? (
          <Alert status="error" mb={4}>
            <AlertIcon />
            Unable to load upcoming matches
          </Alert>
        ) : matchesByMonth.length === 0 ? (
          <Center py={6}>
            <Text color="gray.600" fontSize="sm">No upcoming matches scheduled in the next 3 months</Text>
          </Center>
        ) : (
          <VStack spacing={6} align="stretch">
            {matchesByMonth.map((monthGroup) => (
              <Box key={monthGroup.label}>
                <Text 
                  fontSize="md" 
                  fontWeight="bold" 
                  color="orange.600" 
                  mb={3}
                  borderBottom="2px solid"
                  borderColor="orange.200"
                  pb={1}
                >
                  {monthGroup.label}
                </Text>
                <VStack spacing={2} align="stretch">
                  {monthGroup.matches.map((match) => (
                    <Box
                      key={match.match_id}
                      p={3}
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      bg="white"
                      _hover={{ borderColor: 'orange.500', shadow: 'sm' }}
                      transition="all 0.2s"
                    >
                      <HStack justify="space-between" align="start" spacing={4} flexWrap="wrap">
                        <VStack align="start" spacing={1} flex={1} minW="200px">
                          <HStack spacing={2} flexWrap="wrap">
                            <Text fontWeight="bold" fontSize="md" color="black">
                              {match.match_details.match_name}
                            </Text>
                            {match.match_details.match_status === 'IN_PROGRESS' && (
                              <Badge colorScheme="green" fontSize="xs">In Progress</Badge>
                            )}
                          </HStack>
                          <Link 
                            href={getDisciplineLink(match.match_details.match_discipline)}
                            color="orange.500"
                            fontSize="sm"
                            _hover={{ textDecoration: 'underline' }}
                          >
                            {getDisciplineLabel(match.match_details.match_discipline)}
                          </Link>
                        </VStack>
                        <HStack spacing={4} fontSize="sm" color="gray.600" flexWrap="wrap">
                          <Text whiteSpace="nowrap">📅 {formatDate(match.match_details.match_date)}</Text>
                          {match.match_details.match_location && <Text>📍 {match.match_details.match_location}</Text>}
                        </HStack>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        )}
      </Box>
      
      <Center>
        <Text as="h3" color="#000000" fontSize={{ base: '22px' }} fontWeight="600">{additional.title}</Text>
      </Center>
      <Box pb="6">Questions about our Match Programs? Contact us at <Link href="mailto:competitions@goldengunclub.us">competitions@goldengunclub.us</Link> and our MDs will reach out.</Box>
      <Box>Match Directors:</Box>
      <TableContainer my="16px">
        <Table size='sm'>
          <Tbody>
            <Tr>
              <Td>Black Powder Cartridge</Td>
              <Td>Mike Rix</Td>
            </Tr>
            <Tr>
              <Td>Cowboy Silhouette</Td>
              <Td>Rich DelPizzo</Td>
            </Tr>
            <Tr>
              <Td>Service Rifle</Td>
              <Td>Matt Anthony</Td>
            </Tr>
            <Tr>
              <Td>Bowling Pin Pistol</Td>
              <Td>Craig Smith</Td>
            </Tr>
            <Tr>
              <Td>NRA Action Pistol</Td>
              <Td>Jim Flagg</Td>
            </Tr>
            <Tr>
              <Td>Skeet, 5-stand and Trap Leagues</Td>
              <Td>Jason Nester</Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
      <Grid 
        mt={4} 
        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} 
        gap={6}
      >
        {additional.ctas.map((cta, index) => (
          <ButtonCallToAction
            key={index}
            image={cta.image}
            title={cta.title}
            subtitle={cta.subtitle}
            buttonLink={cta.buttonLink}
            buttonText={cta.buttonText}
          />
        ))}
      </Grid>
    </Box>
  );
};
